import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader, useToaster, Message } from 'rsuite';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import { useMessenger } from '../../context/messengerContext';
import { getContractsFullByID, deleteContract } from '../../api/auth';
import ConfirmModal from '../../components/confirmModal/confirmModal';
import Btn from '../../components/Btn/Btn';

import './contractsList.css';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

export default function ContractsList() {
    const { t } = useTranslation('basic');
    const { userId, platform } = useMessenger();
    const navigate = useNavigate();
    const toaster = useToaster();

    const [contracts, setContracts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [contractToDisable, setContractToDisable] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const loadContracts = async () => {
        setIsLoading(true);
        setIsError(null);
        try {
            const res = await getContractsFullByID({ platform, userId });
            const loadedContracts = Array.isArray(res) ? res : [];
            setContracts(loadedContracts);
            if (loadedContracts.length === 0) {
                navigate('/auth', { replace: true });
            }
        } catch (err) {
            setIsError(err?.message || t('contracts_error'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadContracts();
    }, [userId]);

    const openDisableModal = (contractNumber) => {
        setContractToDisable(contractNumber);
        setDeleteError(null);
        setModalOpen(true);
    };

    const closeDisableModal = () => {
        setModalOpen(false);
        setContractToDisable(null);
        setDeleteError(null);
    };

    const handleConfirmDisable = async () => {
        if (contractToDisable == null || !userId) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await deleteContract({
                platform,
                userId,
                contract_number: contractToDisable,
            });
            closeDisableModal();
            await loadContracts();
            toaster.push(
                <Message type='success' showIcon>
                    {t('contracts_disable_success')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } catch (err) {
            const msg = err?.message || t('contracts_disable_error');
            setDeleteError(msg);
            toaster.push(
                <Message type='error' showIcon>
                    {msg}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const isIOS26Version = isIOSVersionHigherThan26Version();

    if (isLoading) {
        return (
            <div className='loader-container'>
                <Loader size='lg' />
            </div>
        );
    }

    if (isError) {
        return (
            <div className={`contractsList ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
                <BasicPanel>
                    <p className='contractsList_error'>{isError}</p>
                </BasicPanel>
            </div>
        );
    }

    return (
        <div className={`stepsHeaderWrapper gradient_bg_header ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
            <BasicWrapper>
                <div className='contractsList_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('contracts_list_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                </div>

                <div className='contractsList_addBtnContainer'>
                    <Btn
                        text={t('nav_add_contract')}
                        onClick={() => navigate('/auth')}
                        width='100%'
                    />
                </div>

                {contracts.length === 0 && (
                    <div className='contractsList_emptyContainer'>
                        <p className='contractsList_empty'>
                            {t('contracts_empty')}
                        </p>
                    </div>
                )}
                {contracts.length > 0 && (
                    <ul className='contractsList_list'>
                        {contracts.map((item) => (
                            <li
                                key={item.contract_id}
                                className='contractsList_card'
                            >
                                <div className='contractsList_cardRow'>
                                    {t('contracts_contract_number')}:{' '}
                                    <span className='contractsList_cardRowValue'>
                                        {item.contract_number ?? '—'}
                                    </span>
                                </div>
                                <div className='contractsList_cardRow'>
                                    {t('contracts_client_company_name')}:{' '}
                                    <span className='contractsList_cardRowValue'>
                                        {item.client_company_name ?? '—'}
                                    </span>
                                </div>
                                <div className='contractsList_cardActions'>
                                    <Btn
                                        text={t('contracts_disable')}
                                        onClick={() =>
                                            openDisableModal(
                                                item.contract_number,
                                            )
                                        }
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <ConfirmModal
                    open={modalOpen}
                    onClose={closeDisableModal}
                    onConfirm={handleConfirmDisable}
                    titleKey='contracts_disable_confirm_title'
                    bodyKey='contracts_disable_warning'
                    bodyInterpolation={{
                        contract_number: contractToDisable ?? '—',
                    }}
                    confirmTextKey='contracts_disable_confirm_btn'
                    error={deleteError}
                    isLoading={isDeleting}
                />
            </BasicWrapper>
        </div>
    );
}
